/* eslint-disable prettier/prettier */
import Vue from "vue";

export const LOCALES = [
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "lu", label: "LU" },
];

let saved = "en";
try {
  saved = localStorage.getItem("locale") || "en";
} catch (e) {
  saved = "en";
}

export const state = Vue.observable({ locale: saved });

export function setLocale(code) {
  state.locale = code;
  try {
    localStorage.setItem("locale", code);
  } catch (e) {
    /* ignore */
  }
  try {
    document.documentElement.lang = code;
  } catch (e) {
    /* ignore */
  }
}

try {
  document.documentElement.lang = state.locale;
} catch (e) {
  /* ignore */
}

export const messages = {
  en: {
    nav: {
      home: "Home",
      platform: "Platform",
      solutions: "Solutions",
      projects: "Projects",
      about: "About",
      contact: "Contact",
      login: "Login",
    },
    home: {
      eyebrow: "Intelligent Water Control",
      title: "Real-time optimisation for water infrastructure",
      intro:
        "RTC4Water helps utilities monitor, predict and optimise water systems using digital twins and intelligent control.",
      explore: "Explore Platform",
      how: "How It Works",
      dashboard: "Dashboard",
      workflow: "Workflow",
      netOverview: "Network Overview",
      netSub: "Real-time sewer optimisation",
      live: "LIVE",
      overflow: "Overflow Reduction",
      storage: "Storage Utilization",
      response: "Response Time",
      dashEyebrow: "Project Dashboard",
      dashTitle: "One place to manage every sewer-control project",
      dashIntro:
        "Track progress, data readiness, model status and operational KPIs across all projects.",
      orgLabel: "Organisation",
      orgName: "Imagin WasteWater Authority",
      orgDesc:
        "Active DSC implementation with model calibration and controller readiness review.",
      stageLabel: "Current Stage",
      stageName: "Shadow Mode Integration",
      stageProgress: "58% implementation progress",
      readinessLabel: "Readiness Score",
      readinessDesc:
        "Data, model and automation readiness are sufficient for continued controller development.",
      mData: "Data Foundation",
      mDataV: "Good",
      mSwmm: "SWMM Model",
      mSwmmV: "In Review",
      mPlc: "PLC Readiness",
      mPlcV: "Ready",
      mCtrl: "Controller Status",
      mCtrlV: "Shadow Mode",
      wfEyebrow: "Implementation Workflow",
      wfTitle: "A structured path from assessment to live control",
      wfIntro:
        "Each project moves through clearly defined stages, each with readiness checks and clear responsibilities.",
      readiness: "Readiness",
      statusComplete: "complete",
      statusActive: "active",
      statusPlanned: "planned",
      customersTitle: "Our customers & partners",
      stages: [
        {
          title: "Align & Scope",
          desc: "Define objectives, boundaries, stakeholders and the value hypothesis.",
        },
        {
          title: "Build Data Foundation",
          desc: "Collect, validate and structure operational, GIS and SCADA data.",
        },
        {
          title: "Shadow Mode Integration",
          desc: "Connect to live operational systems while calculating recommendations without control.",
        },
        {
          title: "Build Control-Ready Model",
          desc: "Prepare and simplify the SWMM model for predictive control.",
        },
        {
          title: "Generate Controller",
          desc: "Translate the model into constraints, objectives and safe control logic.",
        },
        {
          title: "Commission & Go Live",
          desc: "Move from supervised shadow mode to safe operational control.",
        },
      ],
    },
    platform: {
      badge: "The RTC4Water Implementation Platform",
      titleA: "Guided implementation for intelligent",
      titleB: "water control",
      intro:
        "A structured platform that guides utilities and partners through every stage of real-time water optimisation.",
      requestAccess: "Sign up",
      exploreStages: "Explore the 11 stages",
      challengeLabel: "The Challenge",
      challengeTitle: "Implementation is complex, fragmented and risky",
      challenge: [
        "Many stakeholders and responsibilities",
        "Unclear sequence of activities",
        "High uncertainty and project risk",
        "Difficult to scale across systems and teams",
      ],
      solutionLabel: "The RTC4Water Solution",
      solutionTitle: "A guided path from assessment to live control",
      solution: [
        "Proven 11-stage implementation methodology",
        "Clear deliverables and readiness checks",
        "Built-in tools, templates and best practices",
        "Scalable across utilities, partners and projects",
      ],
      stagesLabel: "11 Implementation Stages",
      stagesTitle: "From assessment to live control",
      stages: [
        { title: "Align & Scope", subtitle: "Value hypothesis" },
        { title: "Build Data Foundation", subtitle: "Data readiness" },
        { title: "Shadow Mode", subtitle: "Read-only control" },
        { title: "Control-Ready Model", subtitle: "SWMM setup" },
        { title: "Generate Controller", subtitle: "Model to control" },
        { title: "Safe Automation", subtitle: "PLC readiness" },
        { title: "Prove Value", subtitle: "Simulation results" },
        { title: "Go-Live", subtitle: "Supervisory control" },
        { title: "Operate & Monitor", subtitle: "Operational insight" },
        { title: "Network Insights", subtitle: "CAPEX / OPEX" },
        { title: "Improve", subtitle: "Continuous learning" },
      ],
      audiencesTitle: "Built for those who deliver",
      audiences: [
        {
          title: "Water Utilities",
          text: "Modernise operations, reduce risk and deliver measurable value.",
        },
        {
          title: "SCADA Integrators",
          text: "Implement with confidence using proven workflows and best practices.",
        },
        {
          title: "Engineering Partners",
          text: "Deliver scalable solutions with structured guidance and reusable assets.",
        },
        {
          title: "Operators",
          text: "Gain visibility, control and insight into running systems.",
        },
      ],
      featuresTitle: "Everything you need to succeed",
      features: [
        {
          title: "Project Workspaces",
          text: "Centralised environments for projects, tasks and collaboration.",
        },
        {
          title: "Templates & Tools",
          text: "Ready-to-use checklists, model templates and implementation resources.",
        },
        {
          title: "Guides & Best Practices",
          text: "Step-by-step guidance built from real-world implementations.",
        },
        {
          title: "Simulation & Validation",
          text: "Test scenarios, prove value and reduce implementation risk.",
        },
        {
          title: "Monitoring & Insights",
          text: "Real-time visibility into performance, risks and opportunities.",
        },
        {
          title: "Expert Support",
          text: "Access to RTC4Water expertise whenever guidance is needed.",
        },
      ],
      ctaTitle: "Ready to start your implementation journey?",
      ctaText:
        "Join the RTC4Water implementation program and move from assessment to live control with confidence.",
      talkExpert: "Talk to an expert",
    },
    solutions: {
      eyebrow: "Solutions",
      title: "Flexible solutions for intelligent water infrastructure",
      intro:
        "Real-time control for water and wastewater networks, plus monitoring, evaluation and platform-based implementation support.",
      hint: "(Click an image to see the artwork full screen)",
      new: "NEW",
      items: [
        {
          label: "Drinking Water",
          title: "Water Network Optimisation",
          description:
            "Real-time optimisation for drinking water networks, supporting peak shaving management, operational efficiency and reliable system performance.",
        },
        {
          label: "Wastewater & Stormwater",
          title: "Dynamic Sewer Control",
          description:
            "Autonomous predictive control for sewer and stormwater systems, coordinating pumping stations, storage, overflow management and control structures during wet weather events.",
        },
        {
          label: "Monitoring",
          title: "Operational Monitoring",
          description:
            "Continuous monitoring tools that help utilities understand network behaviour, identify bottlenecks and improve operational decision-making.",
        },
        {
          label: "Evaluation",
          title: "Evaluation & Assessment Tools",
          description:
            "Structured value assessments, simulations and benchmarking methods to estimate control potential and support investment decisions.",
        },
        {
          label: "Platform",
          title: "Implementation Guidance Platform",
          description:
            "A guided implementation platform that helps utilities, partners and integrators move from assessment to live control in structured stages.",
        },
      ],
    },
    projects: {
      eyebrow: "Projects",
      title: "Real-world implementations and research projects",
      intro:
        "We deploy real-time control and digital-twin solutions for wastewater, stormwater and drinking-water infrastructure.",
      impl: [
        {
          region: "United States / Wastewater Project",
          title: "Illinois Based Wastewater Reclamation District",
          description:
            "Implementation of a dynamic sewer control system and digital twin technology to improve network resilience and optimise pumping coordination during wet weather events.",
          tags: ["Response Time", "Pump coordination"],
        },
        {
          region: "Switzerland / Wastewater Project",
          title: "Rheinfelden ARA, Basel-Land, Switzerland",
          description:
            "Pilot of a Load Based Wastewater Collection System resulting in a significant increase of Sludge Produced, while reducing the overflow volume = reduction in load to the receiving waters.",
          tags: ["Forecasting", "Peak Shaving"],
        },
        {
          region: "Luxembourg / Wastewater Project",
          title: "STEP-Bettenbourg Overflow Minimisation",
          description:
            "Development of a Load Based Overflow Minimisation System, linked to water consumption patterns spread out over multiple communes, during WWTP refurbishing.",
          tags: ["Forecasting", "Peak Shaving"],
        },
      ],
      researchEyebrow: "Research & Innovation",
      researchTitle: "European research projects",
      researchIntro:
        "In addition to field implementations, RTC4Water participates in collaborative research projects that advance water-quality monitoring, digital twins, predictive control and climate-resilient water management.",
      researchType: "INTERREG Research Project",
      visit: "Visit Project Website →",
      research: [
        {
          title: "Water Quality in the Sûre",
          description:
            "A collaborative research project focused on improving water-quality monitoring, data interpretation and decision support for safer and more resilient water systems.",
          tags: ["Water quality", "Decision support", "Monitoring"],
        },
        {
          title: "Data-driven Asset Management",
          description:
            "A collaborative research project supporting proactive water-system management, operational resilience and the transition from reactive to predictive infrastructure control.",
          tags: ["Resilience", "Predictive control", "Digital twins"],
        },
      ],
      processTitle: "From value assessment to full deployment",
      processText:
        "RTC4Water projects typically begin with a structured value assessment, followed by model calibration, integration in shadow mode, controller generation, and phased operational deployment.",
    },
    about: {
      eyebrow: "About RTC4Water",
      title: "Real-time control for resilient water systems",
      intro:
        "RTC4Water develops advanced real-time control and digital twin technologies for wastewater, stormwater, and drinking water networks.",
      whoTitle: "Who we are",
      whoText:
        "RTC4Water was created to help utilities move from reactive network operation towards intelligent, predictive and coordinated control. Our work combines hydraulic engineering, operational data, software development and control optimisation to make water infrastructure more resilient, efficient and environmentally sustainable.",
      capabilities: [
        {
          title: "Digital Twins",
          text: "Hydraulic models connected to operational SCADA environments and real-time data streams.",
        },
        {
          title: "Predictive Control",
          text: "Optimisation algorithms coordinate pumping stations, storage and control structures dynamically.",
        },
        {
          title: "Operational Insight",
          text: "Monitoring and analytics help utilities identify bottlenecks and support better investment decisions.",
        },
      ],
      trust: [
        { value: "15+", label: "Years of RTC development" },
        { value: "MPC", label: "Model Based Predictive Control" },
        { value: "Control", label: "Full Autonomous Control" },
        { value: "Support", label: "Monitoring, Evaluation and Tools" },
      ],
      howTitle: "How we work",
      howText:
        "We combine engineering knowledge, hydraulic models, validated data, SCADA integration and safe automation logic. Our solutions are built step by step: first understanding the network, then proving value in simulation and shadow mode, and finally supporting safe operational control.",
      teamEyebrow: "Our Team",
      teamTitle: "The people behind RTC4Water",
      team: [
        {
          role: "Founder - Product Development",
          bio: "PhD in Computer Science. Expert in dynamic system modeling and predictive control. Former senior researcher at LIST focusing on urban water management.",
        },
        {
          role: "Founder - Operations & Product Development",
          bio: "PhD in Analytical and Environmental Chemistry. Combines scientific expertise with practical experience in sewer network rehabilitation. Former head of Eco-Technology at LIST.",
        },
        {
          role: "Software Engineer - IT & Infrastructure",
          bio: "EPITECH graduate. Expert in Python development, DevOps and software architecture. Former IT researcher at LIST and active member of the tech community.",
        },
        {
          role: "IT Development & Wastewater Solutions",
          bio: "Responsible for on-site project implementation. Passionate about sustainable water systems and efficient field coordination.",
        },
        {
          role: "Operations, Finance & Reporting",
          bio: "Responsible for administration and financial coordination. Brings structure, efficiency and organizational strength to the team.",
        },
        {
          role: "PhD-Student on LinkWater Project",
          bio: "PhD candidate joining the RTC4Water team in 2025. Focuses on intelligent water-network modeling and applied machine learning for the predictive control platform.",
        },
      ],
      ctaTitle: "Want to know more?",
      ctaText:
        "Contact us to discuss how RTC4Water can support your utility, project or partnership.",
      contactUs: "Contact Us",
    },
    contact: {
      eyebrow: "Contact Info / Contact Form / Location and Parking",
      title: "Let’s talk about your water network",
      intro:
        "Contact RTC4Water to discuss real-time control, digital twins, sewer optimisation, value assessments, or potential partnerships.",
      contactHeading: "Contact",
      linkedin: "LinkedIn Page",
      route: "Get directions",
      officeHeading: "Office",
      tel: "Tel:",
      formHeading: "Contact Form",
      formIntro:
        "Send us a message and we will get back to you as soon as possible.",
      nameLabel: "Name *",
      namePh: "Your name",
      emailLabel: "Email *",
      emailPh: "your.email@example.com",
      subjectLabel: "Subject *",
      subjectPh: "How can we help?",
      messageLabel: "Message *",
      messagePh: "Write your message here...",
      gdprHeading: "GDPR Information",
      gdprText:
        "RTC4Water fully supports General Data Protection Regulation policies. RTC4Water will only use the data collected in this form, in this case your name and email address, to contact you in response to your inquiry and will not sell, distribute or use your data for any other purposes other than to conduct direct, relevant communications with you. If you have any questions about how this information will be used or you would like us to remove your name and email address from our database, please send a request to info(at)rtc4water.com and we will respond to you as soon as possible.",
      sendBtn: "Send Email",
      parkingHeading: "Parking",
      parkingBefore:
        "Parking in front of the office is not always possible. Secondary parking is shown in the ",
      parkingCircle: "red circle",
      parkingAfter: ".",
      visitors: [
        {
          title: "Our Address",
          text: "62A, Grand Rue\nL-3394, Roeser\nLuxembourg",
        },
        { title: "By Car", text: "Easy access via\nN31 and A13\nmotorways" },
        {
          title: "By Public Transport",
          text: "Bus lines available\nnearby (Stop:\nRoeser, Kierch)",
        },
        { title: "Office Hours", text: "Monday – Friday\n08:30 – 17:30" },
        {
          title: "Meetings",
          text: "Please contact us\nin advance to\nschedule a meeting.",
        },
      ],
    },
    footer: {
      rights: "All rights reserved",
      imprint: "Imprint",
      privacy: "Privacy",
      terms: "Terms",
      vat: "VAT",
      matricule: "Business ID",
    },
  },
  de: {
    nav: {
      home: "Startseite",
      platform: "Plattform",
      solutions: "Lösungen",
      projects: "Projekte",
      about: "Über uns",
      contact: "Kontakt",
      login: "Anmelden",
    },
    home: {
      eyebrow: "Intelligente Wassersteuerung",
      title: "Echtzeit-Optimierung für Wasserinfrastruktur",
      intro:
        "RTC4Water hilft Versorgern, Wassersysteme mit digitalen Zwillingen und intelligenter Steuerung zu überwachen, vorherzusagen und zu optimieren.",
      explore: "Plattform entdecken",
      how: "So funktioniert's",
      dashboard: "Dashboard",
      workflow: "Ablauf",
      netOverview: "Netzübersicht",
      netSub: "Echtzeit-Kanalnetz-Optimierung",
      live: "LIVE",
      overflow: "Überlauf-Reduktion",
      storage: "Speichernutzung",
      response: "Reaktionszeit",
      dashEyebrow: "Projekt-Dashboard",
      dashTitle: "Alle Kanalsteuerungs-Projekte an einem Ort verwalten",
      dashIntro:
        "Verfolgen Sie Fortschritt, Datenreife, Modellstatus und operative KPIs über alle Projekte hinweg.",
      orgLabel: "Organisation",
      orgName: "Imagin Abwasserbehörde",
      orgDesc:
        "Aktive DSC-Umsetzung mit Modellkalibrierung und Prüfung der Reglerbereitschaft.",
      stageLabel: "Aktuelle Phase",
      stageName: "Shadow-Mode-Integration",
      stageProgress: "58 % Umsetzungsfortschritt",
      readinessLabel: "Reifegrad",
      readinessDesc:
        "Daten-, Modell- und Automatisierungsreife sind ausreichend für die weitere Reglerentwicklung.",
      mData: "Datenbasis",
      mDataV: "Gut",
      mSwmm: "SWMM-Modell",
      mSwmmV: "In Prüfung",
      mPlc: "SPS-Bereitschaft",
      mPlcV: "Bereit",
      mCtrl: "Regler-Status",
      mCtrlV: "Shadow Mode",
      wfEyebrow: "Umsetzungs-Ablauf",
      wfTitle: "Ein strukturierter Weg von der Bewertung zur Live-Steuerung",
      wfIntro:
        "Jedes Projekt durchläuft klar definierte Phasen – jeweils mit Reifeprüfungen und klaren Verantwortlichkeiten.",
      readiness: "Reifegrad",
      statusComplete: "abgeschlossen",
      statusActive: "aktiv",
      statusPlanned: "geplant",
      customersTitle: "Unsere Kunden & Partner",
      stages: [
        {
          title: "Ausrichten & Umfang",
          desc: "Ziele, Grenzen, Beteiligte und die Wert-Hypothese definieren.",
        },
        {
          title: "Datenbasis aufbauen",
          desc: "Betriebs-, GIS- und SCADA-Daten erfassen, validieren und strukturieren.",
        },
        {
          title: "Shadow-Mode-Integration",
          desc: "Mit Live-Systemen verbinden und Empfehlungen berechnen – ohne Steuerung.",
        },
        {
          title: "Steuerbares Modell bauen",
          desc: "Das SWMM-Modell für die prädiktive Steuerung vorbereiten und vereinfachen.",
        },
        {
          title: "Regler erzeugen",
          desc: "Das Modell in Randbedingungen, Ziele und sichere Steuerlogik übersetzen.",
        },
        {
          title: "Inbetriebnahme & Go-Live",
          desc: "Vom überwachten Shadow Mode zur sicheren operativen Steuerung wechseln.",
        },
      ],
    },
    platform: {
      badge: "Die RTC4Water Implementierungs-Plattform",
      titleA: "Geführte Umsetzung für intelligente",
      titleB: "Wassersteuerung",
      intro:
        "Eine strukturierte Plattform, die Versorger und Partner durch jede Phase der Echtzeit-Wasseroptimierung führt.",
      requestAccess: "Registrieren",
      exploreStages: "Die 11 Phasen entdecken",
      challengeLabel: "Die Herausforderung",
      challengeTitle: "Umsetzung ist komplex, fragmentiert und riskant",
      challenge: [
        "Viele Beteiligte und Verantwortlichkeiten",
        "Unklare Reihenfolge der Aktivitäten",
        "Hohe Unsicherheit und Projektrisiko",
        "Schwer über Systeme und Teams zu skalieren",
      ],
      solutionLabel: "Die RTC4Water Lösung",
      solutionTitle: "Ein geführter Weg von der Bewertung zur Live-Steuerung",
      solution: [
        "Bewährte 11-Phasen-Methodik",
        "Klare Ergebnisse und Reifeprüfungen",
        "Integrierte Werkzeuge, Vorlagen und Best Practices",
        "Skalierbar über Versorger, Partner und Projekte",
      ],
      stagesLabel: "11 Umsetzungsphasen",
      stagesTitle: "Von der Bewertung zur Live-Steuerung",
      stages: [
        { title: "Ausrichten & Umfang", subtitle: "Wert-Hypothese" },
        { title: "Datenbasis aufbauen", subtitle: "Datenreife" },
        { title: "Shadow Mode", subtitle: "Nur-Lese-Steuerung" },
        { title: "Steuerbares Modell", subtitle: "SWMM-Aufbau" },
        { title: "Regler erzeugen", subtitle: "Modell zu Steuerung" },
        { title: "Sichere Automatisierung", subtitle: "SPS-Bereitschaft" },
        { title: "Wert nachweisen", subtitle: "Simulationsergebnisse" },
        { title: "Go-Live", subtitle: "Überwachte Steuerung" },
        { title: "Betreiben & Überwachen", subtitle: "Betriebseinblick" },
        { title: "Netz-Einblicke", subtitle: "CAPEX / OPEX" },
        { title: "Verbessern", subtitle: "Kontinuierliches Lernen" },
      ],
      audiencesTitle: "Gebaut für die, die umsetzen",
      audiences: [
        {
          title: "Wasserversorger",
          text: "Betrieb modernisieren, Risiken senken und messbaren Wert liefern.",
        },
        {
          title: "SCADA-Integratoren",
          text: "Sicher umsetzen mit bewährten Abläufen und Best Practices.",
        },
        {
          title: "Ingenieur-Partner",
          text: "Skalierbare Lösungen mit strukturierter Anleitung und wiederverwendbaren Bausteinen.",
        },
        {
          title: "Betreiber",
          text: "Sichtbarkeit, Kontrolle und Einblick in laufende Systeme gewinnen.",
        },
      ],
      featuresTitle: "Alles, was Sie für den Erfolg brauchen",
      features: [
        {
          title: "Projekt-Arbeitsbereiche",
          text: "Zentrale Umgebungen für Projekte, Aufgaben und Zusammenarbeit.",
        },
        {
          title: "Vorlagen & Werkzeuge",
          text: "Einsatzbereite Checklisten, Modellvorlagen und Umsetzungsressourcen.",
        },
        {
          title: "Leitfäden & Best Practices",
          text: "Schritt-für-Schritt-Anleitung aus realen Umsetzungen.",
        },
        {
          title: "Simulation & Validierung",
          text: "Szenarien testen, Wert nachweisen und Umsetzungsrisiko senken.",
        },
        {
          title: "Monitoring & Einblicke",
          text: "Echtzeit-Sicht auf Leistung, Risiken und Chancen.",
        },
        {
          title: "Experten-Support",
          text: "Zugang zur RTC4Water-Expertise, wann immer Unterstützung nötig ist.",
        },
      ],
      ctaTitle: "Bereit, Ihre Umsetzungsreise zu starten?",
      ctaText:
        "Nehmen Sie am RTC4Water-Umsetzungsprogramm teil und gehen Sie sicher von der Bewertung zur Live-Steuerung.",
      talkExpert: "Mit einem Experten sprechen",
    },
    solutions: {
      eyebrow: "Lösungen",
      title: "Flexible Lösungen für intelligente Wasserinfrastruktur",
      intro:
        "Echtzeit-Steuerung für Trink- und Abwassernetze, dazu Monitoring, Bewertung und plattformbasierte Umsetzungsunterstützung.",
      hint: "(Auf ein Bild klicken, um es im Vollbild zu sehen)",
      new: "NEU",
      items: [
        {
          label: "Trinkwasser",
          title: "Wassernetz-Optimierung",
          description:
            "Echtzeit-Optimierung für Trinkwassernetze – für Peak Shaving, Betriebseffizienz und zuverlässige Systemleistung.",
        },
        {
          label: "Abwasser & Regenwasser",
          title: "Dynamische Kanalsteuerung",
          description:
            "Autonome prädiktive Steuerung für Kanal- und Regenwassersysteme, die Pumpstationen, Speicher, Überlaufmanagement und Steuerbauwerke bei Regenwetter koordiniert.",
        },
        {
          label: "Monitoring",
          title: "Operatives Monitoring",
          description:
            "Kontinuierliche Monitoring-Werkzeuge, die Versorgern helfen, das Netzverhalten zu verstehen, Engpässe zu erkennen und Entscheidungen zu verbessern.",
        },
        {
          label: "Bewertung",
          title: "Bewertungs- und Analysetools",
          description:
            "Strukturierte Wertbewertungen, Simulationen und Benchmarking-Methoden, um Steuerungspotenzial abzuschätzen und Investitionsentscheidungen zu stützen.",
        },
        {
          label: "Plattform",
          title: "Umsetzungs-Leitplattform",
          description:
            "Eine geführte Umsetzungsplattform, die Versorgern, Partnern und Integratoren hilft, in strukturierten Phasen von der Bewertung zur Live-Steuerung zu gelangen.",
        },
      ],
    },
    projects: {
      eyebrow: "Projekte",
      title: "Reale Umsetzungen und Forschungsprojekte",
      intro:
        "Wir setzen Echtzeit-Steuerung und Digital-Twin-Lösungen für Abwasser-, Regenwasser- und Trinkwasserinfrastruktur um.",
      impl: [
        {
          region: "USA / Abwasserprojekt",
          title: "Illinois Based Wastewater Reclamation District",
          description:
            "Umsetzung eines dynamischen Kanalsteuerungssystems und digitaler-Zwilling-Technologie, um die Netzresilienz zu erhöhen und die Pumpenkoordination bei Regenwetter zu optimieren.",
          tags: ["Reaktionszeit", "Pumpenkoordination"],
        },
        {
          region: "Schweiz / Abwasserprojekt",
          title: "Rheinfelden ARA, Basel-Land, Schweiz",
          description:
            "Pilotprojekt eines lastbasierten Abwassersammelsystems, das zu einer deutlichen Steigerung des anfallenden Klärschlamms führt und zugleich das Überlaufvolumen reduziert – also die Belastung der Vorfluter verringert.",
          tags: ["Prognose", "Peak Shaving"],
        },
        {
          region: "Luxemburg / Abwasserprojekt",
          title: "STEP-Bettemburg Überlauf-Minimierung",
          description:
            "Entwicklung eines lastbasierten Überlauf-Minimierungssystems, gekoppelt an Wasserverbrauchsmuster, verteilt über mehrere Gemeinden, während der Sanierung der Kläranlage.",
          tags: ["Prognose", "Peak Shaving"],
        },
      ],
      researchEyebrow: "Forschung & Innovation",
      researchTitle: "Europäische Forschungsprojekte",
      researchIntro:
        "Neben Feldeinsätzen beteiligt sich RTC4Water an gemeinsamen Forschungsprojekten, die Wasserqualitäts-Monitoring, digitale Zwillinge, prädiktive Steuerung und klimaresilientes Wassermanagement voranbringen.",
      researchType: "INTERREG-Forschungsprojekt",
      visit: "Projekt-Website besuchen →",
      research: [
        {
          title: "Wasserqualität in der Sauer",
          description:
            "Ein gemeinsames Forschungsprojekt zur Verbesserung von Wasserqualitäts-Monitoring, Dateninterpretation und Entscheidungsunterstützung für sicherere und resilientere Wassersysteme.",
          tags: ["Wasserqualität", "Entscheidungshilfe", "Monitoring"],
        },
        {
          title: "Datengetriebenes Asset-Management",
          description:
            "Ein gemeinsames Forschungsprojekt für proaktives Wassersystem-Management, operative Resilienz und den Übergang von reaktiver zu prädiktiver Infrastruktursteuerung.",
          tags: ["Resilienz", "Prädiktive Steuerung", "Digitale Zwillinge"],
        },
      ],
      processTitle: "Von der Wertbewertung bis zur vollen Umsetzung",
      processText:
        "RTC4Water-Projekte beginnen meist mit einer strukturierten Wertbewertung, gefolgt von Modellkalibrierung, Integration im Shadow Mode, Reglererzeugung und schrittweiser operativer Einführung.",
    },
    about: {
      eyebrow: "Über RTC4Water",
      title: "Echtzeit-Steuerung für resiliente Wassersysteme",
      intro:
        "RTC4Water entwickelt fortschrittliche Echtzeit-Steuerung und Digital-Twin-Technologien für Abwasser-, Regenwasser- und Trinkwassernetze.",
      whoTitle: "Wer wir sind",
      whoText:
        "RTC4Water wurde gegründet, um Versorger vom reaktiven Netzbetrieb hin zu intelligenter, prädiktiver und koordinierter Steuerung zu bringen. Unsere Arbeit verbindet Hydraulik-Engineering, Betriebsdaten, Softwareentwicklung und Steuerungsoptimierung, um Wasserinfrastruktur resilienter, effizienter und umweltverträglicher zu machen.",
      capabilities: [
        {
          title: "Digitale Zwillinge",
          text: "Hydraulische Modelle, verbunden mit operativen SCADA-Umgebungen und Echtzeit-Datenströmen.",
        },
        {
          title: "Prädiktive Steuerung",
          text: "Optimierungsalgorithmen koordinieren Pumpstationen, Speicher und Steuerbauwerke dynamisch.",
        },
        {
          title: "Operativer Einblick",
          text: "Monitoring und Analysen helfen Versorgern, Engpässe zu erkennen und bessere Investitionsentscheidungen zu treffen.",
        },
      ],
      trust: [
        { value: "15+", label: "Jahre RTC-Entwicklung" },
        { value: "MPC", label: "Modellbasierte prädiktive Steuerung" },
        { value: "Steuerung", label: "Voll autonome Steuerung" },
        { value: "Support", label: "Monitoring, Bewertung und Werkzeuge" },
      ],
      howTitle: "Wie wir arbeiten",
      howText:
        "Wir verbinden Ingenieurwissen, hydraulische Modelle, validierte Daten, SCADA-Integration und sichere Automatisierungslogik. Unsere Lösungen entstehen Schritt für Schritt: erst das Netz verstehen, dann den Wert in Simulation und Shadow Mode nachweisen und schließlich sichere operative Steuerung unterstützen.",
      teamEyebrow: "Unser Team",
      teamTitle: "Die Menschen hinter RTC4Water",
      team: [
        {
          role: "Gründer - Produktentwicklung",
          bio: "Doktor der Informatik. Experte für dynamische Systemmodellierung und prädiktive Steuerung. Ehemals leitender Forscher am LIST mit Fokus auf urbanes Wassermanagement.",
        },
        {
          role: "Gründer - Betrieb & Produktentwicklung",
          bio: "Doktor der analytischen und Umweltchemie. Verbindet wissenschaftliche Expertise mit praktischer Erfahrung in der Kanalnetz-Sanierung. Ehemals Leiter Eco-Technology am LIST.",
        },
        {
          role: "Software-Ingenieur - IT & Infrastruktur",
          bio: "EPITECH-Absolvent. Experte für Python-Entwicklung, DevOps und Softwarearchitektur. Ehemals IT-Forscher am LIST und aktives Mitglied der Tech-Community.",
        },
        {
          role: "IT-Entwicklung & Abwasserlösungen",
          bio: "Verantwortlich für die Projektumsetzung vor Ort. Begeistert von nachhaltigen Wassersystemen und effizienter Feldkoordination.",
        },
        {
          role: "Betrieb, Finanzen & Reporting",
          bio: "Verantwortlich für Verwaltung und Finanzkoordination. Bringt Struktur, Effizienz und organisatorische Stärke ins Team.",
        },
        {
          role: "Doktorand im LinkWater-Projekt",
          bio: "Doktorand, der 2025 zum RTC4Water-Team stößt. Fokus auf intelligente Wassernetz-Modellierung und angewandtes maschinelles Lernen für die prädiktive Steuerungsplattform.",
        },
      ],
      ctaTitle: "Mehr erfahren?",
      ctaText:
        "Kontaktieren Sie uns, um zu besprechen, wie RTC4Water Ihren Versorger, Ihr Projekt oder Ihre Partnerschaft unterstützen kann.",
      contactUs: "Kontakt aufnehmen",
    },
    contact: {
      eyebrow: "Kontaktinfo / Kontaktformular / Anfahrt und Parken",
      title: "Sprechen wir über Ihr Wassernetz",
      intro:
        "Kontaktieren Sie RTC4Water, um über Echtzeit-Steuerung, digitale Zwillinge, Kanaloptimierung, Wertbewertungen oder mögliche Partnerschaften zu sprechen.",
      contactHeading: "Kontakt",
      linkedin: "LinkedIn-Seite",
      route: "Route planen",
      officeHeading: "Büro",
      tel: "Tel.:",
      formHeading: "Kontaktformular",
      formIntro:
        "Senden Sie uns eine Nachricht und wir melden uns so schnell wie möglich zurück.",
      nameLabel: "Name *",
      namePh: "Ihr Name",
      emailLabel: "E-Mail *",
      emailPh: "ihre.email@beispiel.com",
      subjectLabel: "Betreff *",
      subjectPh: "Wie können wir helfen?",
      messageLabel: "Nachricht *",
      messagePh: "Schreiben Sie hier Ihre Nachricht...",
      gdprHeading: "DSGVO-Information",
      gdprText:
        "RTC4Water unterstützt die Datenschutz-Grundverordnung vollständig. RTC4Water nutzt die in diesem Formular erhobenen Daten – hier Ihren Namen und Ihre E-Mail-Adresse – ausschließlich, um Sie als Antwort auf Ihre Anfrage zu kontaktieren, und wird Ihre Daten nicht verkaufen, weitergeben oder für andere Zwecke als eine direkte, relevante Kommunikation mit Ihnen verwenden. Bei Fragen zur Nutzung dieser Daten oder wenn Sie möchten, dass wir Ihren Namen und Ihre E-Mail-Adresse aus unserer Datenbank löschen, senden Sie bitte eine Anfrage an info(at)rtc4water.com und wir antworten Ihnen so schnell wie möglich.",
      sendBtn: "E-Mail senden",
      parkingHeading: "Parken",
      parkingBefore:
        "Das Parken vor dem Büro ist nicht immer möglich. Das sekundäre Parken wird im ",
      parkingCircle: "roten Kreis",
      parkingAfter: " angezeigt.",
      visitors: [
        {
          title: "Unsere Adresse",
          text: "62A, Grand Rue\nL-3394, Roeser\nLuxemburg",
        },
        { title: "Mit dem Auto", text: "Gut erreichbar über\nN31 und A13\nAutobahnen" },
        {
          title: "Mit dem ÖPNV",
          text: "Buslinien in der Nähe\n(Haltestelle:\nRoeser, Kierch)",
        },
        { title: "Öffnungszeiten", text: "Montag – Freitag\n08:30 – 17:30" },
        {
          title: "Termine",
          text: "Bitte kontaktieren Sie\nuns vorab, um einen\nTermin zu vereinbaren.",
        },
      ],
    },
    footer: {
      rights: "Alle Rechte vorbehalten",
      imprint: "Impressum",
      privacy: "Datenschutz",
      terms: "AGB",
      vat: "USt-IdNr.",
      matricule: "Handelsregister-Nr.",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      platform: "Plateforme",
      solutions: "Solutions",
      projects: "Projets",
      about: "À propos",
      contact: "Contact",
      login: "Connexion",
    },
    home: {
      eyebrow: "Contrôle intelligent de l'eau",
      title: "Optimisation en temps réel pour les infrastructures de l'eau",
      intro:
        "RTC4Water aide les gestionnaires à surveiller, prévoir et optimiser les systèmes d'eau grâce aux jumeaux numériques et au contrôle intelligent.",
      explore: "Découvrir la plateforme",
      how: "Comment ça marche",
      dashboard: "Tableau de bord",
      workflow: "Processus",
      netOverview: "Vue du réseau",
      netSub: "Optimisation du réseau en temps réel",
      live: "EN DIRECT",
      overflow: "Réduction des débordements",
      storage: "Utilisation du stockage",
      response: "Temps de réponse",
      dashEyebrow: "Tableau de bord de projet",
      dashTitle:
        "Gérer tous les projets de contrôle des réseaux au même endroit",
      dashIntro:
        "Suivez l'avancement, la maturité des données, l'état des modèles et les KPI opérationnels sur tous les projets.",
      orgLabel: "Organisation",
      orgName: "Autorité des eaux usées Imagin",
      orgDesc:
        "Mise en œuvre active du DSC avec calibrage du modèle et revue de préparation du contrôleur.",
      stageLabel: "Étape actuelle",
      stageName: "Intégration en mode fantôme",
      stageProgress: "58 % d'avancement",
      readinessLabel: "Niveau de préparation",
      readinessDesc:
        "La maturité des données, du modèle et de l'automatisation est suffisante pour poursuivre le développement du contrôleur.",
      mData: "Base de données",
      mDataV: "Bon",
      mSwmm: "Modèle SWMM",
      mSwmmV: "En revue",
      mPlc: "Préparation API",
      mPlcV: "Prêt",
      mCtrl: "État du contrôleur",
      mCtrlV: "Mode fantôme",
      wfEyebrow: "Processus de mise en œuvre",
      wfTitle: "Un parcours structuré de l'évaluation au contrôle en direct",
      wfIntro:
        "Chaque projet suit des étapes clairement définies, avec des contrôles de préparation et des responsabilités claires.",
      readiness: "Préparation",
      statusComplete: "terminé",
      statusActive: "actif",
      statusPlanned: "planifié",
      customersTitle: "Nos clients & partenaires",
      stages: [
        {
          title: "Cadrage & objectifs",
          desc: "Définir les objectifs, le périmètre, les parties prenantes et l'hypothèse de valeur.",
        },
        {
          title: "Construire la base de données",
          desc: "Collecter, valider et structurer les données d'exploitation, SIG et SCADA.",
        },
        {
          title: "Intégration en mode fantôme",
          desc: "Se connecter aux systèmes en temps réel et calculer des recommandations sans contrôle.",
        },
        {
          title: "Modèle prêt au contrôle",
          desc: "Préparer et simplifier le modèle SWMM pour le contrôle prédictif.",
        },
        {
          title: "Générer le contrôleur",
          desc: "Traduire le modèle en contraintes, objectifs et logique de contrôle sûre.",
        },
        {
          title: "Mise en service & lancement",
          desc: "Passer du mode fantôme supervisé au contrôle opérationnel sûr.",
        },
      ],
    },
    platform: {
      badge: "La plateforme de mise en œuvre RTC4Water",
      titleA: "Mise en œuvre guidée pour un",
      titleB: "contrôle intelligent de l'eau",
      intro:
        "Une plateforme structurée qui guide les gestionnaires et partenaires à chaque étape de l'optimisation de l'eau en temps réel.",
      requestAccess: "S'inscrire",
      exploreStages: "Découvrir les 11 étapes",
      challengeLabel: "Le défi",
      challengeTitle: "La mise en œuvre est complexe, fragmentée et risquée",
      challenge: [
        "De nombreuses parties prenantes et responsabilités",
        "Une séquence d'activités peu claire",
        "Une forte incertitude et un risque projet élevé",
        "Difficile à déployer à l'échelle des systèmes et équipes",
      ],
      solutionLabel: "La solution RTC4Water",
      solutionTitle: "Un parcours guidé de l'évaluation au contrôle en direct",
      solution: [
        "Méthodologie éprouvée en 11 étapes",
        "Livrables clairs et contrôles de préparation",
        "Outils, modèles et bonnes pratiques intégrés",
        "Déployable à l'échelle des gestionnaires, partenaires et projets",
      ],
      stagesLabel: "11 étapes de mise en œuvre",
      stagesTitle: "De l'évaluation au contrôle en direct",
      stages: [
        { title: "Cadrage & objectifs", subtitle: "Hypothèse de valeur" },
        { title: "Base de données", subtitle: "Maturité des données" },
        { title: "Mode fantôme", subtitle: "Contrôle en lecture seule" },
        { title: "Modèle prêt au contrôle", subtitle: "Config. SWMM" },
        { title: "Générer le contrôleur", subtitle: "Du modèle au contrôle" },
        { title: "Automatisation sûre", subtitle: "Préparation API" },
        { title: "Prouver la valeur", subtitle: "Résultats de simulation" },
        { title: "Mise en service", subtitle: "Contrôle supervisé" },
        { title: "Exploiter & surveiller", subtitle: "Vision opérationnelle" },
        { title: "Analyses du réseau", subtitle: "CAPEX / OPEX" },
        { title: "Améliorer", subtitle: "Apprentissage continu" },
      ],
      audiencesTitle: "Conçu pour ceux qui réalisent",
      audiences: [
        {
          title: "Gestionnaires de l'eau",
          text: "Moderniser l'exploitation, réduire les risques et créer une valeur mesurable.",
        },
        {
          title: "Intégrateurs SCADA",
          text: "Mettre en œuvre en confiance avec des flux éprouvés et de bonnes pratiques.",
        },
        {
          title: "Partenaires d'ingénierie",
          text: "Livrer des solutions évolutives avec un accompagnement structuré et des ressources réutilisables.",
        },
        {
          title: "Opérateurs",
          text: "Gagner en visibilité, en contrôle et en compréhension des systèmes en service.",
        },
      ],
      featuresTitle: "Tout ce qu'il faut pour réussir",
      features: [
        {
          title: "Espaces de projet",
          text: "Environnements centralisés pour les projets, les tâches et la collaboration.",
        },
        {
          title: "Modèles & outils",
          text: "Check-lists, modèles et ressources de mise en œuvre prêts à l'emploi.",
        },
        {
          title: "Guides & bonnes pratiques",
          text: "Un accompagnement étape par étape issu de mises en œuvre réelles.",
        },
        {
          title: "Simulation & validation",
          text: "Tester des scénarios, prouver la valeur et réduire le risque.",
        },
        {
          title: "Suivi & analyses",
          text: "Visibilité en temps réel sur la performance, les risques et les opportunités.",
        },
        {
          title: "Support expert",
          text: "Accès à l'expertise RTC4Water dès qu'un accompagnement est nécessaire.",
        },
      ],
      ctaTitle: "Prêt à démarrer votre parcours de mise en œuvre ?",
      ctaText:
        "Rejoignez le programme de mise en œuvre RTC4Water et passez en confiance de l'évaluation au contrôle en direct.",
      talkExpert: "Parler à un expert",
    },
    solutions: {
      eyebrow: "Solutions",
      title: "Des solutions flexibles pour une infrastructure de l'eau intelligente",
      intro:
        "Contrôle en temps réel des réseaux d'eau et d'assainissement, avec suivi, évaluation et accompagnement de mise en œuvre par plateforme.",
      hint: "(Cliquez sur une image pour la voir en plein écran)",
      new: "NOUVEAU",
      items: [
        {
          label: "Eau potable",
          title: "Optimisation du réseau d'eau",
          description:
            "Optimisation en temps réel des réseaux d'eau potable, au service de l'écrêtage des pointes, de l'efficacité opérationnelle et d'une performance fiable.",
        },
        {
          label: "Assainissement & eaux pluviales",
          title: "Contrôle dynamique des réseaux",
          description:
            "Contrôle prédictif autonome des réseaux d'assainissement et pluviaux, coordonnant stations de pompage, stockage, gestion des débordements et ouvrages de contrôle lors des épisodes pluvieux.",
        },
        {
          label: "Suivi",
          title: "Suivi opérationnel",
          description:
            "Des outils de suivi continu qui aident les gestionnaires à comprendre le comportement du réseau, repérer les goulets d'étranglement et améliorer les décisions.",
        },
        {
          label: "Évaluation",
          title: "Outils d'évaluation et d'analyse",
          description:
            "Évaluations de valeur structurées, simulations et méthodes de comparaison pour estimer le potentiel de contrôle et éclairer les décisions d'investissement.",
        },
        {
          label: "Plateforme",
          title: "Plateforme d'accompagnement",
          description:
            "Une plateforme de mise en œuvre guidée qui aide gestionnaires, partenaires et intégrateurs à passer de l'évaluation au contrôle en direct par étapes structurées.",
        },
      ],
    },
    projects: {
      eyebrow: "Projets",
      title: "Réalisations concrètes et projets de recherche",
      intro:
        "Nous déployons le contrôle en temps réel et des solutions de jumeaux numériques pour les infrastructures d'assainissement, pluviales et d'eau potable.",
      impl: [
        {
          region: "États-Unis / Projet d'assainissement",
          title: "Illinois Based Wastewater Reclamation District",
          description:
            "Mise en œuvre d'un système de contrôle dynamique des réseaux et de la technologie de jumeau numérique pour renforcer la résilience du réseau et optimiser la coordination du pompage lors des épisodes pluvieux.",
          tags: ["Temps de réponse", "Coordination du pompage"],
        },
        {
          region: "Suisse / Projet d'assainissement",
          title: "Rheinfelden ARA, Bâle-Campagne, Suisse",
          description:
            "Projet pilote d'un système de collecte des eaux usées basé sur la charge, entraînant une augmentation significative des boues produites tout en réduisant le volume de débordement – soit une réduction de la charge rejetée dans les eaux réceptrices.",
          tags: ["Prévision", "Écrêtage des pointes"],
        },
        {
          region: "Luxembourg / Projet d'assainissement",
          title: "STEP-Bettembourg : minimisation des débordements",
          description:
            "Développement d'un système de minimisation des débordements basé sur la charge, lié aux profils de consommation d'eau répartis sur plusieurs communes, pendant la rénovation de la station d'épuration.",
          tags: ["Prévision", "Écrêtage des pointes"],
        },
      ],
      researchEyebrow: "Recherche & innovation",
      researchTitle: "Projets de recherche européens",
      researchIntro:
        "Au-delà des déploiements de terrain, RTC4Water participe à des projets de recherche collaboratifs qui font progresser le suivi de la qualité de l'eau, les jumeaux numériques, le contrôle prédictif et une gestion de l'eau résiliente au climat.",
      researchType: "Projet de recherche INTERREG",
      visit: "Visiter le site du projet →",
      research: [
        {
          title: "Qualité de l'eau dans la Sûre",
          description:
            "Un projet de recherche collaboratif visant à améliorer le suivi de la qualité de l'eau, l'interprétation des données et l'aide à la décision pour des systèmes d'eau plus sûrs et plus résilients.",
          tags: ["Qualité de l'eau", "Aide à la décision", "Suivi"],
        },
        {
          title: "Gestion d'actifs pilotée par les données",
          description:
            "Un projet de recherche collaboratif soutenant une gestion proactive des systèmes d'eau, la résilience opérationnelle et le passage d'un contrôle réactif à prédictif.",
          tags: ["Résilience", "Contrôle prédictif", "Jumeaux numériques"],
        },
      ],
      processTitle: "De l'évaluation de la valeur au déploiement complet",
      processText:
        "Les projets RTC4Water commencent généralement par une évaluation structurée de la valeur, suivie de la calibration du modèle, de l'intégration en mode fantôme, de la génération du contrôleur et d'un déploiement opérationnel par étapes.",
    },
    about: {
      eyebrow: "À propos de RTC4Water",
      title: "Contrôle en temps réel pour des systèmes d'eau résilients",
      intro:
        "RTC4Water développe des technologies avancées de contrôle en temps réel et de jumeaux numériques pour les réseaux d'assainissement, pluviaux et d'eau potable.",
      whoTitle: "Qui nous sommes",
      whoText:
        "RTC4Water a été créé pour aider les gestionnaires à passer d'une exploitation réactive du réseau à un contrôle intelligent, prédictif et coordonné. Notre travail combine ingénierie hydraulique, données d'exploitation, développement logiciel et optimisation du contrôle pour rendre l'infrastructure de l'eau plus résiliente, efficace et durable.",
      capabilities: [
        {
          title: "Jumeaux numériques",
          text: "Des modèles hydrauliques connectés aux environnements SCADA opérationnels et aux flux de données en temps réel.",
        },
        {
          title: "Contrôle prédictif",
          text: "Des algorithmes d'optimisation coordonnent dynamiquement les stations de pompage, le stockage et les ouvrages de contrôle.",
        },
        {
          title: "Vision opérationnelle",
          text: "Le suivi et l'analyse aident les gestionnaires à repérer les goulets d'étranglement et à mieux décider des investissements.",
        },
      ],
      trust: [
        { value: "15+", label: "Ans de développement RTC" },
        { value: "MPC", label: "Contrôle prédictif basé sur modèle" },
        { value: "Contrôle", label: "Contrôle entièrement autonome" },
        { value: "Support", label: "Suivi, évaluation et outils" },
      ],
      howTitle: "Notre façon de travailler",
      howText:
        "Nous combinons savoir-faire d'ingénierie, modèles hydrauliques, données validées, intégration SCADA et logique d'automatisation sûre. Nos solutions se construisent étape par étape : d'abord comprendre le réseau, puis prouver la valeur en simulation et en mode fantôme, et enfin soutenir un contrôle opérationnel sûr.",
      teamEyebrow: "Notre équipe",
      teamTitle: "Les personnes derrière RTC4Water",
      team: [
        {
          role: "Fondateur - Développement produit",
          bio: "Docteur en informatique. Expert en modélisation de systèmes dynamiques et en contrôle prédictif. Ancien chercheur senior au LIST, spécialisé dans la gestion de l'eau urbaine.",
        },
        {
          role: "Fondateur - Opérations & Développement produit",
          bio: "Docteur en chimie analytique et environnementale. Allie expertise scientifique et expérience pratique de la réhabilitation des réseaux d'assainissement. Ancien responsable Eco-Technology au LIST.",
        },
        {
          role: "Ingénieur logiciel - IT & Infrastructure",
          bio: "Diplômé d'EPITECH. Expert en développement Python, DevOps et architecture logicielle. Ancien chercheur IT au LIST et membre actif de la communauté tech.",
        },
        {
          role: "Développement IT & Solutions d'assainissement",
          bio: "Responsable de la mise en œuvre des projets sur le terrain. Passionné par les systèmes d'eau durables et une coordination de terrain efficace.",
        },
        {
          role: "Opérations, Finance & Reporting",
          bio: "Responsable de l'administration et de la coordination financière. Apporte structure, efficacité et solidité organisationnelle à l'équipe.",
        },
        {
          role: "Doctorant sur le projet LinkWater",
          bio: "Doctorant rejoignant l'équipe RTC4Water en 2025. Se concentre sur la modélisation intelligente des réseaux d'eau et l'apprentissage automatique appliqué à la plateforme de contrôle prédictif.",
        },
      ],
      ctaTitle: "En savoir plus ?",
      ctaText:
        "Contactez-nous pour voir comment RTC4Water peut soutenir votre gestion, votre projet ou votre partenariat.",
      contactUs: "Nous contacter",
    },
    contact: {
      eyebrow: "Coordonnées / Formulaire de contact / Accès et parking",
      title: "Parlons de votre réseau d'eau",
      intro:
        "Contactez RTC4Water pour échanger sur le contrôle en temps réel, les jumeaux numériques, l'optimisation des réseaux, les évaluations de valeur ou d'éventuels partenariats.",
      contactHeading: "Contact",
      linkedin: "Page LinkedIn",
      route: "Itinéraire",
      officeHeading: "Bureau",
      tel: "Tél. :",
      formHeading: "Formulaire de contact",
      formIntro:
        "Envoyez-nous un message et nous vous répondrons dès que possible.",
      nameLabel: "Nom *",
      namePh: "Votre nom",
      emailLabel: "E-mail *",
      emailPh: "votre.email@exemple.com",
      subjectLabel: "Objet *",
      subjectPh: "Comment pouvons-nous aider ?",
      messageLabel: "Message *",
      messagePh: "Écrivez votre message ici...",
      gdprHeading: "Information RGPD",
      gdprText:
        "RTC4Water respecte pleinement le Règlement général sur la protection des données. RTC4Water n'utilisera les données collectées dans ce formulaire – ici votre nom et votre adresse e-mail – que pour vous contacter en réponse à votre demande, et ne vendra, ne distribuera ni n'utilisera vos données à d'autres fins qu'une communication directe et pertinente avec vous. Pour toute question sur l'utilisation de ces informations, ou si vous souhaitez que nous supprimions votre nom et votre adresse e-mail de notre base de données, envoyez une demande à info(at)rtc4water.com et nous vous répondrons dès que possible.",
      sendBtn: "Envoyer l'e-mail",
      parkingHeading: "Parking",
      parkingBefore:
        "Le stationnement devant le bureau n'est pas toujours possible. Le stationnement secondaire est indiqué dans le ",
      parkingCircle: "cercle rouge",
      parkingAfter: ".",
      visitors: [
        {
          title: "Notre adresse",
          text: "62A, Grand Rue\nL-3394, Roeser\nLuxembourg",
        },
        {
          title: "En voiture",
          text: "Accès facile via\nles autoroutes\nN31 et A13",
        },
        {
          title: "En transports en commun",
          text: "Lignes de bus à\nproximité (Arrêt :\nRoeser, Kierch)",
        },
        { title: "Horaires", text: "Lundi – Vendredi\n08:30 – 17:30" },
        {
          title: "Rendez-vous",
          text: "Merci de nous contacter\nà l'avance pour fixer\nun rendez-vous.",
        },
      ],
    },
    footer: {
      rights: "Tous droits réservés",
      imprint: "Mentions légales",
      privacy: "Confidentialité",
      terms: "Conditions",
      vat: "TVA",
      matricule: "N° d'immatriculation",
    },
  },
  lu: {
    nav: {
      home: "Haaptsäit",
      platform: "Plattform",
      solutions: "Léisungen",
      projects: "Projeten",
      about: "Iwwer eis",
      contact: "Kontakt",
      login: "Umellen",
    },
    home: {
      eyebrow: "Intelligent Waasserkontroll",
      title: "Echtzäit-Optiméierung fir Waasserinfrastruktur",
      intro:
        "RTC4Water hëlleft de Versuergungsbetriber, Waassersystemer mat digitalen Zwillingen an intelligenter Steierung ze iwwerwaachen, virauszesoen an ze optiméieren.",
      explore: "Plattform entdecken",
      how: "Wéi et funktionéiert",
      dashboard: "Dashboard",
      workflow: "Oflaf",
      netOverview: "Netziwwersiicht",
      netSub: "Echtzäit-Kanalnetz-Optiméierung",
      live: "LIVE",
      overflow: "Iwwerlaf-Reduktioun",
      storage: "Späichernotzung",
      response: "Reaktiounszäit",
      dashEyebrow: "Projet-Dashboard",
      dashTitle: "All Kanalsteierungs-Projeten op enger Plaz verwalten",
      dashIntro:
        "Verfollegt Fortschrëtt, Datereife, Modellstatus an operativ KPIen iwwer all Projeten.",
      orgLabel: "Organisatioun",
      orgName: "Imagin Ofwaasser-Autoritéit",
      orgDesc:
        "Aktiv DSC-Ëmsetzung mat Modellkalibréierung a Kontroll vun der Regler-Bereetschaft.",
      stageLabel: "Aktuell Phase",
      stageName: "Shadow-Mode-Integratioun",
      stageProgress: "58 % Ëmsetzungsfortschrëtt",
      readinessLabel: "Reifegrad",
      readinessDesc:
        "Date-, Modell- an Automatiséierungsreife si genuch fir déi weider Regler-Entwécklung.",
      mData: "Datebasis",
      mDataV: "Gutt",
      mSwmm: "SWMM-Modell",
      mSwmmV: "An der Iwwerpréifung",
      mPlc: "SPS-Bereetschaft",
      mPlcV: "Prett",
      mCtrl: "Regler-Status",
      mCtrlV: "Shadow Mode",
      wfEyebrow: "Ëmsetzungs-Oflaf",
      wfTitle: "E strukturéierte Wee vun der Bewäertung bis zur Live-Steierung",
      wfIntro:
        "All Projet leeft duerch kloer definéiert Phasen – mat Reifekontrollen a kloere Verantwortlechkeeten.",
      readiness: "Reifegrad",
      statusComplete: "ofgeschloss",
      statusActive: "aktiv",
      statusPlanned: "geplangt",
      customersTitle: "Eis Cliente & Partner",
      stages: [
        {
          title: "Ausriichten & Ëmfang",
          desc: "Ziler, Grenzen, Bedeelegt an d'Wäert-Hypothes definéieren.",
        },
        {
          title: "Datebasis opbauen",
          desc: "Betriebs-, GIS- a SCADA-Donnéeë sammelen, validéieren a strukturéieren.",
        },
        {
          title: "Shadow-Mode-Integratioun",
          desc: "Mat Live-Systemer verbannen an Empfehlungen berechnen – ouni Steierung.",
        },
        {
          title: "Steierbaart Modell bauen",
          desc: "D'SWMM-Modell fir déi prädiktiv Steierung virbereeden a vereinfachen.",
        },
        {
          title: "Regler erstellen",
          desc: "D'Modell an Randbedéngungen, Ziler a sécher Steierlogik iwwersetzen.",
        },
        {
          title: "Inbetriebnahm & Go-Live",
          desc: "Vum iwwerwaachte Shadow Mode op déi sécher operativ Steierung wiesselen.",
        },
      ],
    },
    platform: {
      badge: "Déi RTC4Water Ëmsetzungs-Plattform",
      titleA: "Gefouert Ëmsetzung fir intelligent",
      titleB: "Waassersteierung",
      intro:
        "Eng strukturéiert Plattform, déi Versuergungsbetriber a Partner duerch all Phase vun der Echtzäit-Waasseroptiméierung féiert.",
      requestAccess: "Registréieren",
      exploreStages: "Déi 11 Phasen entdecken",
      challengeLabel: "D'Erausfuerderung",
      challengeTitle: "D'Ëmsetzung ass komplex, fragmentéiert a riskant",
      challenge: [
        "Vill Bedeelegt a Verantwortlechkeeten",
        "Onkloer Reiefolleg vun den Aktivitéiten",
        "Grouss Onsécherheet a Projet-Risiko",
        "Schwéier iwwer Systemer an Ekippen ze skaléieren",
      ],
      solutionLabel: "Déi RTC4Water Léisung",
      solutionTitle: "E gefouerte Wee vun der Bewäertung bis zur Live-Steierung",
      solution: [
        "Bewäert 11-Phasen-Method",
        "Kloer Resultater a Reifekontrollen",
        "Integréiert Tools, Virlagen a Best Practices",
        "Skaléierbar iwwer Versuergungsbetriber, Partner a Projeten",
      ],
      stagesLabel: "11 Ëmsetzungsphasen",
      stagesTitle: "Vun der Bewäertung bis zur Live-Steierung",
      stages: [
        { title: "Ausriichten & Ëmfang", subtitle: "Wäert-Hypothes" },
        { title: "Datebasis opbauen", subtitle: "Datereife" },
        { title: "Shadow Mode", subtitle: "Nëmme-Liese-Steierung" },
        { title: "Steierbaart Modell", subtitle: "SWMM-Opbau" },
        { title: "Regler erstellen", subtitle: "Modell zu Steierung" },
        { title: "Sécher Automatiséierung", subtitle: "SPS-Bereetschaft" },
        { title: "Wäert beweisen", subtitle: "Simulatiounsresultater" },
        { title: "Go-Live", subtitle: "Iwwerwaacht Steierung" },
        { title: "Bedreiwen & Iwwerwaachen", subtitle: "Betribsabléck" },
        { title: "Netz-Abléck", subtitle: "CAPEX / OPEX" },
        { title: "Verbesseren", subtitle: "Kontinuéierlecht Léieren" },
      ],
      audiencesTitle: "Gebaut fir déi, déi ëmsetzen",
      audiences: [
        {
          title: "Waasserversuerger",
          text: "Betrib moderniséieren, Risiken reduzéieren a moossbare Wäert liwweren.",
        },
        {
          title: "SCADA-Integratoren",
          text: "Sécher ëmsetzen mat bewäerten Oflaf a Best Practices.",
        },
        {
          title: "Ingenieur-Partner",
          text: "Skaléierbar Léisunge mat strukturéierter Uleedung a widderverwendbare Bausteng.",
        },
        {
          title: "Bedreiber",
          text: "Siichtbarkeet, Kontroll an Abléck an d'lafend Systemer kréien.",
        },
      ],
      featuresTitle: "Alles, wat Dir fir den Erfolleg braucht",
      features: [
        {
          title: "Projet-Aarbechtsberäicher",
          text: "Zentral Ëmfeld fir Projeten, Aufgaben a Zesummenaarbecht.",
        },
        {
          title: "Virlagen & Tools",
          text: "Asazbereet Checklëschten, Modellvirlagen an Ëmsetzungsressourcen.",
        },
        {
          title: "Guiden & Best Practices",
          text: "Schrëtt-fir-Schrëtt-Uleedung aus richtegen Ëmsetzungen.",
        },
        {
          title: "Simulatioun & Validéierung",
          text: "Szenarien testen, Wäert beweisen an Ëmsetzungsrisiko reduzéieren.",
        },
        {
          title: "Monitoring & Abléck",
          text: "Echtzäit-Vue op Leeschtung, Risiken a Chancen.",
        },
        {
          title: "Experten-Support",
          text: "Zougang zur RTC4Water-Expertise, wann ëmmer Ënnerstëtzung néideg ass.",
        },
      ],
      ctaTitle: "Prett fir Är Ëmsetzungsrees ze starten?",
      ctaText:
        "Maacht beim RTC4Water-Ëmsetzungsprogramm mat a gitt sécher vun der Bewäertung bis zur Live-Steierung.",
      talkExpert: "Mat engem Expert schwätzen",
    },
    solutions: {
      eyebrow: "Léisungen",
      title: "Flexibel Léisunge fir eng intelligent Waasserinfrastruktur",
      intro:
        "Echtzäit-Steierung fir Drénk- an Ofwaassernetzer, dozou Monitoring, Bewäertung a plattformbaséiert Ëmsetzungsënnerstëtzung.",
      hint: "(Op e Bild klicken, fir et am Vollbild ze gesinn)",
      new: "NEI",
      items: [
        {
          label: "Drénkwaasser",
          title: "Waassernetz-Optiméierung",
          description:
            "Echtzäit-Optiméierung fir Drénkwaassernetzer – fir Peak Shaving, Betribseffizienz an zouverlässeg Systemleeschtung.",
        },
        {
          label: "Ofwaasser & Reewaasser",
          title: "Dynamesch Kanalsteierung",
          description:
            "Autonom prädiktiv Steierung fir Kanal- a Reewaassersystemer, déi Pompelstatiounen, Späicher, Iwwerlafmanagement a Steierbauwierker bei Reewieder koordinéiert.",
        },
        {
          label: "Monitoring",
          title: "Operatiivt Monitoring",
          description:
            "Kontinuéierlech Monitoring-Tools, déi de Versuerger hëllefen, d'Netzverhalen ze verstoen, Engpäss z'erkennen an Decisiounen ze verbesseren.",
        },
        {
          label: "Bewäertung",
          title: "Bewäertungs- an Analysetools",
          description:
            "Strukturéiert Wäertbewäertungen, Simulatiounen a Benchmarking-Methoden, fir d'Steierungspotenzial anzeschätzen an Investitiounsentscheedungen ze stäipen.",
        },
        {
          label: "Plattform",
          title: "Ëmsetzungs-Leetplattform",
          description:
            "Eng gefouert Ëmsetzungsplattform, déi Versuerger, Partner an Integratoren hëlleft, a strukturéierte Phasen vun der Bewäertung bis zur Live-Steierung ze kommen.",
        },
      ],
    },
    projects: {
      eyebrow: "Projeten",
      title: "Real Ëmsetzungen a Fuerschungsprojeten",
      intro:
        "Mir setzen Echtzäit-Steierung an Digital-Twin-Léisunge fir Ofwaasser-, Reewaasser- an Drénkwaasserinfrastruktur ëm.",
      impl: [
        {
          region: "USA / Ofwaasserprojet",
          title: "Illinois Based Wastewater Reclamation District",
          description:
            "Ëmsetzung vun engem dynamesche Kanalsteierungssystem an Digital-Twin-Technologie, fir d'Netzresilienz ze erhéijen an d'Pompelkoordinatioun bei Reewieder ze optiméieren.",
          tags: ["Reaktiounszäit", "Pompelkoordinatioun"],
        },
        {
          region: "Schwäiz / Ofwaasserprojet",
          title: "Rheinfelden ARA, Basel-Land, Schwäiz",
          description:
            "Pilotprojet vun engem laaschtbaséierten Ofwaasser-Sammelsystem, dat zu enger däitlecher Erhéijung vum produzéierte Klärschlamm féiert an zugläich d'Iwwerlafvolume reduzéiert – also d'Belaaschtung vun de Vorfluter verréngert.",
          tags: ["Prognos", "Peak Shaving"],
        },
        {
          region: "Lëtzebuerg / Ofwaasserprojet",
          title: "STEP-Bettemburg Iwwerlaf-Minimiséierung",
          description:
            "Entwécklung vun engem laaschtbaséierten Iwwerlaf-Minimiséierungssystem, gekoppelt un d'Waasserverbrauchsmuster, verdeelt iwwer méi Gemengen, während der Sanéierung vun der Kläranlag.",
          tags: ["Prognos", "Peak Shaving"],
        },
      ],
      researchEyebrow: "Fuerschung & Innovatioun",
      researchTitle: "Europäesch Fuerschungsprojeten",
      researchIntro:
        "Nieft de Feldasätz bedeelegt sech RTC4Water u gemeinsame Fuerschungsprojeten, déi Waasserqualitéits-Monitoring, digital Zwillingen, prädiktiv Steierung a klimaresilient Waassermanagement viruntreiwen.",
      researchType: "INTERREG-Fuerschungsprojet",
      visit: "Projet-Websäit besichen →",
      research: [
        {
          title: "Waasserqualitéit an der Sauer",
          description:
            "E gemeinsame Fuerschungsprojet fir d'Verbesserung vum Waasserqualitéits-Monitoring, der Dateninterpretatioun an der Entscheedungshëllef fir méi sécher a resilient Waassersystemer.",
          tags: ["Waasserqualitéit", "Entscheedungshëllef", "Monitoring"],
        },
        {
          title: "Datebaséiert Asset-Management",
          description:
            "E gemeinsame Fuerschungsprojet fir proaktiivt Waassersystem-Management, operativ Resilienz an den Iwwergang vu reaktiver op prädiktiv Infrastruktursteierung.",
          tags: ["Resilienz", "Prädiktiv Steierung", "Digital Zwillingen"],
        },
      ],
      processTitle: "Vun der Wäertbewäertung bis zur voller Ëmsetzung",
      processText:
        "RTC4Water-Projeten fänken meeschtens mat enger strukturéierter Wäertbewäertung un, gefollegt vu Modellkalibréierung, Integratioun am Shadow Mode, Reglererzeegung an enger schrëttweiser operativer Aféierung.",
    },
    about: {
      eyebrow: "Iwwer RTC4Water",
      title: "Echtzäit-Steierung fir resilient Waassersystemer",
      intro:
        "RTC4Water entwéckelt fortgeschratt Echtzäit-Steierung an Digital-Twin-Technologien fir Ofwaasser-, Reewaasser- an Drénkwaassernetzer.",
      whoTitle: "Wie mir sinn",
      whoText:
        "RTC4Water gouf gegrënnt fir de Versuergungsbetriber ze hëllefen, vum reaktiven Netzbetrib op eng intelligent, prädiktiv a koordinéiert Steierung ze wiesselen. Eis Aarbecht verbënnt Hydraulik-Ingenieurwiesen, Betribsdaten, Softwareentwécklung a Steierungsoptiméierung, fir d'Waasserinfrastruktur méi resilient, effizient an ëmweltverträglech ze maachen.",
      capabilities: [
        {
          title: "Digital Zwillingen",
          text: "Hydraulesch Modeller, verbonne mat operative SCADA-Ëmfelder an Echtzäit-Datestréim.",
        },
        {
          title: "Prädiktiv Steierung",
          text: "Optiméierungsalgorithmen koordinéieren Pompelstatiounen, Späicher a Steierbauwierker dynamesch.",
        },
        {
          title: "Operativen Abléck",
          text: "Monitoring an Analysen hëllefen de Versuerger, Engpäss z'erkennen a besser Investitiounsentscheedungen ze treffen.",
        },
      ],
      trust: [
        { value: "15+", label: "Joer RTC-Entwécklung" },
        { value: "MPC", label: "Modellbaséiert prädiktiv Steierung" },
        { value: "Steierung", label: "Voll autonom Steierung" },
        { value: "Support", label: "Monitoring, Bewäertung an Tools" },
      ],
      howTitle: "Wéi mir schaffen",
      howText:
        "Mir verbannen Ingenieurwëssen, hydraulesch Modeller, validéiert Daten, SCADA-Integratioun a sécher Automatiséierungslogik. Eis Léisunge gi Schrëtt fir Schrëtt gebaut: fir d'éischt d'Netz verstoen, dann de Wäert an der Simulatioun an am Shadow Mode beweisen, a schlussendlech eng sécher operativ Steierung ënnerstëtzen.",
      teamEyebrow: "Eist Team",
      teamTitle: "D'Leit hannert RTC4Water",
      team: [
        {
          role: "Grënner - Produktentwécklung",
          bio: "Dokter an der Informatik. Expert fir dynamesch Systemmodelléierung a prädiktiv Steierung. Fréier Senior-Fuerscher um LIST mat Fokus op urbaant Waassermanagement.",
        },
        {
          role: "Grënner - Betrib & Produktentwécklung",
          bio: "Dokter an der analytescher an Ëmweltchimie. Verbënnt wëssenschaftlech Expertise mat praktescher Erfarung an der Kanalnetz-Sanéierung. Fréier Chef Eco-Technology um LIST.",
        },
        {
          role: "Software-Ingenieur - IT & Infrastruktur",
          bio: "EPITECH-Diplomé. Expert fir Python-Entwécklung, DevOps a Softwarearchitektur. Fréier IT-Fuerscher um LIST an aktivt Member vun der Tech-Community.",
        },
        {
          role: "IT-Entwécklung & Ofwaasser-Léisungen",
          bio: "Responsabel fir d'Projet-Ëmsetzung op der Plaz. Passionéiert vun nohaltege Waassersystemer an enger effizienter Feldkoordinatioun.",
        },
        {
          role: "Betrib, Finanzen & Reporting",
          bio: "Responsabel fir Verwaltung a Finanzkoordinatioun. Bréngt Struktur, Effizienz an organisatoresch Stäerkt an d'Team.",
        },
        {
          role: "Doktorand am LinkWater-Projet",
          bio: "Doktorand, deen 2025 bei d'RTC4Water-Team stéisst. Fokus op intelligent Waassernetz-Modelléierung an ugewandt Maschinnléieren fir déi prädiktiv Steierungsplattform.",
        },
      ],
      ctaTitle: "Méi wëssen?",
      ctaText:
        "Kontaktéiert eis, fir ze kucken, wéi RTC4Water Äre Versuerger, Äre Projet oder Är Partnerschaft ënnerstëtze kann.",
      contactUs: "Kontakt ophuelen",
    },
    contact: {
      eyebrow: "Kontaktinfo / Kontaktformular / Uféierung a Parken",
      title: "Schwätze mir iwwer Äert Waassernetz",
      intro:
        "Kontaktéiert RTC4Water fir iwwer Echtzäit-Steierung, digital Zwillingen, Kanaloptiméierung, Wäertbewäertungen oder méiglech Partnerschaften ze schwätzen.",
      contactHeading: "Kontakt",
      linkedin: "LinkedIn-Säit",
      route: "Streck plangen",
      officeHeading: "Büro",
      tel: "Tel.:",
      formHeading: "Kontaktformular",
      formIntro:
        "Schéckt eis eng Noriicht a mir mellen eis esou séier wéi méiglech zréck.",
      nameLabel: "Numm *",
      namePh: "Äre Numm",
      emailLabel: "E-Mail *",
      emailPh: "är.email@beispill.com",
      subjectLabel: "Sujet *",
      subjectPh: "Wéi kënne mir hëllefen?",
      messageLabel: "Noriicht *",
      messagePh: "Schreift hei Är Noriicht...",
      gdprHeading: "DSGVO-Informatioun",
      gdprText:
        "RTC4Water ënnerstëtzt d'Dateschutz-Grondveruerdnung voll. RTC4Water benotzt d'Donnéeën, déi an dësem Formular gesammelt ginn – hei Äre Numm an Är E-Mail-Adress – nëmme fir Iech als Äntwert op Är Ufro ze kontaktéieren, a wäert Är Donnéeën net verkafen, weiderginn oder fir aner Zwecker wéi eng direkt, relevant Kommunikatioun mat Iech benotzen. Bei Froen zur Notzung vun dësen Informatiounen, oder wann Dir wëllt datt mir Äre Numm an Är E-Mail-Adress aus eiser Datebank läschen, schéckt w.e.g. eng Ufro un info(at)rtc4water.com a mir äntweren Iech esou séier wéi méiglech.",
      sendBtn: "E-Mail schécken",
      parkingHeading: "Parken",
      parkingBefore:
        "D'Parke virum Büro ass net ëmmer méiglech. De sekundäre Parking gëtt am ",
      parkingCircle: "roude Krees",
      parkingAfter: " gewisen.",
      visitors: [
        {
          title: "Eis Adress",
          text: "62A, Grand Rue\nL-3394, Roeser\nLëtzebuerg",
        },
        {
          title: "Mam Auto",
          text: "Gutt erreechbar iwwer\nd'Autobunnen\nN31 an A13",
        },
        {
          title: "Mam ëffentlechen Transport",
          text: "Buslinnen niewendrun\n(Arrêt:\nRoeser, Kierch)",
        },
        { title: "Ëffnungszäiten", text: "Méindeg – Freideg\n08:30 – 17:30" },
        {
          title: "Rendez-vousen",
          text: "Kontaktéiert eis w.e.g.\nam Viraus, fir en\nRendez-vous ze maachen.",
        },
      ],
    },
    footer: {
      rights: "All Rechter virbehalen",
      imprint: "Impressum",
      privacy: "Dateschutz",
      terms: "Konditiounen",
      vat: "TVA",
      matricule: "Handelsregëster-Nr.",
    },
  },
};

export function t(key) {
  const parts = key.split(".");
  const pick = (obj) => parts.reduce((o, k) => (o == null ? o : o[k]), obj);
  const val = pick(messages[state.locale]);
  if (val != null) return val;
  const fallback = pick(messages.en);
  return fallback != null ? fallback : key;
}

export default { state, messages, t, setLocale, LOCALES };
